import { useState, useEffect, useRef } from 'react'

import type _Account from 'mobx/Account'
import type { AuthenticatorStatusKeys } from 'mobx/Account/Account.type'
import type _Application from 'mobx/Application'
import type _Infra from 'mobx/Infra'

import oStorage from 'utils/o-storage'

export const AUTHENTICATOR_STATUS = {
    UNSTARTED: 'unstarted',
	DISCONNECTED: 'disconnected',
	WAITING: 'waiting',
	CONNECTED: 'connected',
	EXPIRED: 'expired',
}

const AUTHENTICATOR_STORAGE_KEY = 'authenticatorConnectionState'

const useAuthenticator = (Application: typeof _Application, Account: typeof _Account, onDisconnection?: () => void) => {
    const initialCodeExpiresDuration = 30
    const factor = 100 / initialCodeExpiresDuration

    const statusDisconnectedPollingTime = 4000
    const statusConnectedPollingTime = 15000
    const defaultConnectionState = {
        status: AUTHENTICATOR_STATUS.UNSTARTED,
        keys: null,
        code: null,
    }
    const savedConnectionState: { status: string; keys: AuthenticatorStatusKeys; code: string, lastSave: number } = oStorage.get(AUTHENTICATOR_STORAGE_KEY)

    const [codeExpiresIn, setCodeExpiresIn] = useState(initialCodeExpiresDuration)
    const [progress, setProgress] = useState(100)
    const [connectionState, setConnectionState] = useState(savedConnectionState || defaultConnectionState)
    const [unknownExpirationTime, setUnknownExpirationTime] = useState(connectionState.status === AUTHENTICATOR_STATUS.WAITING)

    const initied = useRef(false)
    const statusIsFetching = useRef(false)
    const askedDisconnection = useRef(false)
    const timerRef = useRef<NodeJS.Timer | null>(null)
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null)

    const isConnected = connectionState.status === AUTHENTICATOR_STATUS.CONNECTED

    // Timer to handle the authenticator status polling
    useEffect(() => {
        if (initied.current && connectionState.keys && connectionState.lastSave) {
            statusTimerRef.current = setTimeout(
                async () => {
                    await handleStatus()
                },
                isConnected ? statusConnectedPollingTime : statusDisconnectedPollingTime
            )
        }

        return () => {
            clearTimeout(statusTimerRef.current as NodeJS.Timeout)
        }
    }, [initied.current, connectionState.keys, connectionState.lastSave])

    // Timer to handle the displayed expiration time
    useEffect(() => {
        if (initied.current && connectionState.code && unknownExpirationTime === false) {
            timerRef.current = setInterval(() => {
                setCodeExpiresIn((oldCodeExpiresIn) => oldCodeExpiresIn - 1)
            }, 1000)
        }

        return () => {
            clearInterval(timerRef.current as NodeJS.Timer)
        }
    }, [initied.current, connectionState.code, unknownExpirationTime])

    // Progress bar animation and restart timer handler
    useEffect(() => {
        setProgress(codeExpiresIn * factor)
        if (codeExpiresIn === 0) {
            stop()
            handleStatus()
        }
    }, [codeExpiresIn])

    useEffect(() => {
        if(connectionState.status === AUTHENTICATOR_STATUS.DISCONNECTED && askedDisconnection.current === false) {
            if(onDisconnection !== undefined) {
                onDisconnection()
            }
        }
    }, [connectionState.status])

    const init = async () => {
        initied.current = true

        const status = await handleStatus()
        if (status === AUTHENTICATOR_STATUS.WAITING) {
            setUnknownExpirationTime(true)
        }
    }

    const save = (connectionState: any) => {
        const lastSave = new Date().getTime()
        const lastConnectionState = {
            ...connectionState,
            lastSave,
        }

        // Save the connection state at component level
        setConnectionState(lastConnectionState)

        // Save the connection state in browser memory
        oStorage.set(AUTHENTICATOR_STORAGE_KEY, lastConnectionState)
    }

    const stop = () => {
        clearInterval(timerRef.current as NodeJS.Timer)
        clearTimeout(statusTimerRef.current as NodeJS.Timeout)
    }

    const start = async () => {
        reset()

        const { code, waitingKey, connectedKey } = await getCode()

        save({
            status: AUTHENTICATOR_STATUS.WAITING,
            keys: { waitingKey, connectedKey },
            code,
        })

        setUnknownExpirationTime(false)
        setCodeExpiresIn(initialCodeExpiresDuration)
    }

    const reset = () => {
        save(defaultConnectionState)
        stop()
        setCodeExpiresIn(initialCodeExpiresDuration)
    }

    const disconected = () => {
        save({
            keys: null,
            status: AUTHENTICATOR_STATUS.DISCONNECTED,
            code: null,
        })
        stop()
        setCodeExpiresIn(initialCodeExpiresDuration)
    }

    const handleStatus = async () => {
        if (!connectionState.keys) {
            start()
            return
        }

        if (statusIsFetching.current === true) {
            return
        }

        const status = await getStatus(connectionState.keys)

        if (askedDisconnection.current === true) {
            return
        }

        if (status === AUTHENTICATOR_STATUS.WAITING || status === AUTHENTICATOR_STATUS.CONNECTED) {
            save({
                ...connectionState,
                status,
            })
        }

        if (status === AUTHENTICATOR_STATUS.EXPIRED) {
            start()
        }

        if (status === AUTHENTICATOR_STATUS.DISCONNECTED) {
            disconected()
        }

        return status
    }

    const getCode = async () => Account.getAuthenticatorCode(Application.backendChannel)

    const getStatus = async (keys: AuthenticatorStatusKeys): Promise<string> => {
        statusIsFetching.current = true
        const status = await Account.getAuthenticatorStatus(Application.backendChannel, keys)
        statusIsFetching.current = false

        return status
    }

    const disconnect = async (): Promise<void> => {
        askedDisconnection.current = true
        await Account.disconnectAuthenticator(Application.backendChannel, connectionState.keys)
        askedDisconnection.current = false
        await start()
    }

    return {
        isConnected, connectionState, progress, unknownExpirationTime, codeExpiresIn, start: init, disconnect
    }
}

export default useAuthenticator
